import Layout from "@/pages/layouts/application"
import { router, usePage, useForm } from "@inertiajs/react"
import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useDropzone } from 'react-dropzone'
import { DirectUpload } from "@rails/activestorage"
import { toast } from "sonner"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/core/page_header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  FileText, X, Upload, MoreHorizontal,
  Link, Cloud, Terminal, ShoppingCart, Brain, Wallet, Heart, Leaf,
  Mic, Store, Building2, Layers, TrendingUp, GitBranch,
  Landmark, Briefcase, ChevronDown, ChevronUp,
  Image as ImageIcon, Video, Play, Pause,
} from "lucide-react"
import { SourceFavicon } from "@/components/source_favicon"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog"

import { format } from "date-fns"
import highlightsApi from "@/api/Organizations/HighlightsApi"
import * as api from "@/api"
import VoiceNoteWidget from "@/components/audio/voice_note_widget"

const periodOptions = [
  { value: "last_week", label: "Last Week" },
  { value: "this_week", label: "This Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "last_three_months", label: "Last 3 Months" },
]

const DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads"
const VOICE_MEMO_POLL_MS = 5000
const VOICE_MEMO_POLL_MAX_ATTEMPTS = 36

const IDEA_FILTER_KINDS = ["web_clip", "voice_note", "podcast", "note", "video", "image", "meeting"]
const KIND_META = {
  web_clip: { label: "Reading Highlights" },
  voice_note: { label: "Voice Notes" },
  podcast: { label: "Audio" },
  note: { label: "Text" },
  video: { label: "Videos" },
  image: { label: "Images" },
  meeting: { label: "Meetings" },
}

const TOPIC_META = {
  saas: { label: "SaaS", icon: Cloud },
  devtools: { label: "Dev Tools", icon: Terminal },
  ecommerce: { label: "E-Commerce & DTC", icon: ShoppingCart },
  ai_ml: { label: "AI & Machine Learning", icon: Brain },
  fintech: { label: "Fintech & Payments", icon: Wallet },
  healthtech: { label: "Health & Biotech", icon: Heart },
  climate_tech: { label: "Climate & Energy", icon: Leaf },
  creator_economy: { label: "Creator Economy", icon: Mic },
  marketplaces: { label: "Marketplaces & Platforms", icon: Store },
  enterprise_sales: { label: "Enterprise & B2B Sales", icon: Building2 },
  product_management: { label: "Product & Design", icon: Layers },
  growth_marketing: { label: "Growth & Marketing", icon: TrendingUp },
  open_source: { label: "Open Source", icon: GitBranch },
  venture_capital: { label: "Venture Capital", icon: Landmark },
  agency_consulting: { label: "Agency & Consulting", icon: Briefcase },
}

const SOURCE_KIND_LABELS = {
  substack: "Substack",
  linkedin_account: "LinkedIn",
  hn_topic: "HN topic",
  reddit_community: "Reddit",
  blog: "Blog",
  newsletter: "Newsletter",
}

const getCsrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : null
}

const getErrorMessage = (errors) => Object.values(errors || {}).flat().join(", ") || "Unknown error"
const stopEvent = (event) => event.stopPropagation()

const directUploadFile = (file, { onSuccess, onError }) => {
  const csrfToken = getCsrfToken()
  const uploader = new DirectUpload(file, DIRECT_UPLOAD_URL, {
    directUploadWillStoreFileWithXHR: (request) => {
      request.setRequestHeader("X-CSRF-Token", csrfToken)
    },
  })

  uploader.create((error, blob) => {
    if (error) {
      onError(error)
      return
    }

    onSuccess(blob)
  })
}

function SectionHeading({ title, titleClassName = "text-sm font-medium text-muted-foreground", actions, align = "center" }) {
  return (
    <div className={`flex ${align === "start" ? "items-start" : "items-center"} justify-between gap-3`}>
      {title ? <h3 className={titleClassName}>{title}</h3> : <div />}
      {actions}
    </div>
  )
}

function EmptyState({ children, className = "" }) {
  return (
    <div className={`flex min-h-[180px] items-center justify-center rounded-md border border-dashed p-6 text-center text-base text-muted-foreground ${className}`}>
      {children}
    </div>
  )
}

function EditTextModal({ isOpen, highlight, onClose, onSave, isSaving }) {
  const [textContent, setTextContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [removeExistingMedia, setRemoveExistingMedia] = useState(false);

  useEffect(() => {
    if (highlight) {
      setTextContent(highlight.text || highlight.transcript || '');
      setSelectedFile(null);
      setFilePreview(null);
      setRemoveExistingMedia(false);
    }
  }, [highlight]);

  const existingMediaUrl = highlight?.asset?.file_url;
  const existingMediaType = highlight?.kind;
  const existingAudioUrl = ["voice_note", "podcast"].includes(existingMediaType)
    ? (existingMediaUrl || highlight?.recording_url || null)
    : null;

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setRemoveExistingMedia(true);
      
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        const previewUrl = URL.createObjectURL(file);
        setFilePreview({ url: previewUrl, type: file.type });
      } else {
        setFilePreview({ url: null, type: file.type, name: file.name });
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isSaving
  });

  const clearFile = () => {
    if (filePreview?.url) {
      URL.revokeObjectURL(filePreview.url);
    }
    setSelectedFile(null);
    setFilePreview(null);
  };

  const clearExistingMedia = () => {
    setRemoveExistingMedia(true);
  };

  const restoreExistingMedia = () => {
    setRemoveExistingMedia(false);
    clearFile();
  };

  const handleSave = () => {
    onSave(highlight.id, textContent, selectedFile, removeExistingMedia);
  };

  const handleClose = () => {
    clearFile();
    onClose();
  };

  useEffect(() => {
    return () => {
      if (filePreview?.url) {
        URL.revokeObjectURL(filePreview.url);
      }
    };
  }, [filePreview]);

  if (!isOpen || !highlight) return null;

  const showExistingMedia = existingMediaUrl && !removeExistingMedia && !selectedFile;
  const showNewFilePreview = selectedFile && filePreview;
  const showDropzone = !showExistingMedia && !showNewFilePreview;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]">
        <DialogHeader>
          <DialogTitle>Edit Idea</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-text">Content</Label>
            <Textarea
              id="edit-text"
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder="Outline your thesis..."
              rows={4}
              disabled={isSaving}
              className="max-h-[40vh] overflow-y-auto"
            />
          </div>

          <div className="space-y-2">
            <Label>Media (optional)</Label>
            
            {showExistingMedia && !existingAudioUrl && (
              <div className="relative border rounded-lg p-3 bg-muted/30">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={clearExistingMedia}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                </Button>
                {(existingMediaType === 'image') && (
                  <img src={existingMediaUrl} alt="Current media" className="max-h-40 rounded object-contain mx-auto" />
                )}
                {(existingMediaType === 'video') && (
                  <video src={existingMediaUrl} className="max-h-40 rounded mx-auto" controls />
                )}
              </div>
            )}

            {showNewFilePreview && (
              <div className="relative border rounded-lg p-3 bg-muted/30">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1 right-1 h-6 w-6"
                  onClick={existingMediaUrl ? restoreExistingMedia : clearFile}
                  disabled={isSaving}
                >
                  <X className="h-4 w-4" />
                </Button>
                {filePreview?.type?.startsWith('image/') && filePreview.url && (
                  <img src={filePreview.url} alt="Preview" className="max-h-40 rounded object-contain mx-auto" />
                )}
                {filePreview?.type?.startsWith('video/') && filePreview.url && (
                  <video src={filePreview.url} className="max-h-40 rounded mx-auto" controls />
                )}
                {!filePreview?.url && (
                  <div className="flex items-center gap-2 py-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{selectedFile.name}</span>
                  </div>
                )}
              </div>
            )}

            {showDropzone && (
              <div
                {...getRootProps()}
                className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors ${
                  isDragActive ? 'border-primary bg-muted/60' : 'border-muted-foreground/25'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="h-6 w-6 text-muted-foreground mb-1" />
                <p className="text-sm text-muted-foreground">
                  {isDragActive ? 'Drop the file here...' : 'Drag & drop or click to upload'}
                </p>
              </div>
            )}

            {existingAudioUrl && !removeExistingMedia && !selectedFile && (
              <div className="flex items-center gap-2">
                <audio
                  src={existingAudioUrl}
                  controls
                  className="h-8 min-w-0 flex-1 rounded-md bg-black"
                  style={{ colorScheme: "dark" }}
                >
                  Your browser does not support the audio element.
                </audio>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                  onClick={clearExistingMedia}
                  disabled={isSaving}
                  aria-label="Remove audio"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={handleClose} disabled={isSaving}>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AddHighlightModal({ isOpen, onClose, onSubmit, isSubmitting }) {
  const [noteText, setNoteText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const onDrop = useCallback(acceptedFiles => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        const previewUrl = URL.createObjectURL(file);
        setFilePreview({ url: previewUrl, type: file.type });
      } else {
        setFilePreview({ url: null, type: file.type, name: file.name });
      }
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    disabled: isSubmitting
  });

  const clearFile = () => {
    if (filePreview?.url) {
      URL.revokeObjectURL(filePreview.url);
    }
    setSelectedFile(null);
    setFilePreview(null);
  };

  const handleSubmit = () => {
    onSubmit(noteText, selectedFile);
  };

  const handleClose = () => {
    setNoteText('');
    clearFile();
    onClose();
  };

  useEffect(() => {
    return () => {
      if (filePreview?.url) {
        URL.revokeObjectURL(filePreview.url);
      }
    };
  }, [filePreview]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]">
        <DialogHeader>
          <DialogTitle className='text-sm'>Add Idea</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Textarea
              id="highlight-text"
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Add rough ideas, blog posts, call transcripts, etc"
              rows={4}
              disabled={isSubmitting}
              className="max-h-[40vh] overflow-y-auto"
            />
          </div>

          <div className="space-y-2">
            {!selectedFile ? (
              <div
                {...getRootProps()}
                className={`flex h-32 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed bg-muted/30 transition-colors hover:bg-muted/50 ${
                  isDragActive ? 'border-primary bg-muted/60' : 'border-muted-foreground/25'
                }`}
              >
                <input {...getInputProps()} />
                <Upload className="mb-2 h-6 w-6 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {isDragActive ? 'Drop the file here...' : 'Drag & drop or click to upload'}
                </p>
              </div>
            ) : (
              <div className="relative rounded-lg border bg-muted/30 p-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-6 w-6"
                  onClick={clearFile}
                  disabled={isSubmitting}
                >
                  <X className="h-4 w-4" />
                </Button>

                {filePreview?.type?.startsWith('image/') && filePreview.url && (
                  <img
                    src={filePreview.url}
                    alt="Preview"
                    className="mx-auto max-h-40 rounded object-contain"
                  />
                )}
                {filePreview?.type?.startsWith('video/') && filePreview.url && (
                  <video
                    src={filePreview.url}
                    className="mx-auto max-h-40 rounded"
                    controls
                  />
                )}
                {!filePreview?.url && (
                  <div className="flex items-center gap-2 py-2">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{selectedFile.name}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || (!noteText.trim() && !selectedFile)}
          >
            {isSubmitting ? 'Saving...' : 'Save Idea'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SourceRow({ source, orgNanoId, onEdit }) {
  const toggleActive = (next) => {
    router.patch(
      api.organizationsMediaSources.update.path({
        org_nano_id: orgNanoId,
        id: source.id,
      }),
      { media_source: { active: next } },
      { preserveScroll: true }
    )
  }

  const remove = () => {
    if (!confirm(`Remove "${source.name}"?`)) return
    router.delete(
      api.organizationsMediaSources.destroy.path({
        org_nano_id: orgNanoId,
        id: source.id,
      }),
      { preserveScroll: true }
    )
  }
  return (
    <div
      className="flex h-full cursor-pointer flex-col gap-3 rounded-md border p-4 transition-[background-color,border-color,box-shadow] duration-200 hover:bg-accent/50 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
      onClick={() => onEdit(source)}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <SourceFavicon url={source.url} name={source.name} />
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={stopEvent}
            className="truncate font-medium text-sm transition-colors hover:text-primary"
            title={source.url}
          >
            {source.name}
          </a>
        </div>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onPointerDown={stopEvent}
              onMouseDown={stopEvent}
              onClick={stopEvent}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-0" onCloseAutoFocus={stopEvent} onClick={stopEvent}>
            <DropdownMenuItem onSelect={(event) => {
              event.preventDefault()
              onEdit(source)
            }}>
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault()
                remove()
              }}
              className="text-destructive focus:text-destructive"
            >
              Remove
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="mt-auto flex items-center justify-between gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              onPointerDown={stopEvent}
              onMouseDown={stopEvent}
              onClick={stopEvent}
              className="flex items-center gap-2 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              <span className={`size-2 rounded-full ${source.active ? "bg-green-500" : "bg-muted-foreground/40"}`} />
              <span>{source.active ? "Reading every 12h" : "Paused"}</span>
              <ChevronDown className="size-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0.5" align="start" onClick={stopEvent}>
            <button
              type="button"
              onClick={(event) => {
                stopEvent(event)
                toggleActive(!source.active)
              }}
              className="px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            >
              {source.active ? "Pause" : "Resume"}
            </button>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  )
}

function EditSourceModal({ isOpen, source, onClose, orgNanoId, kinds }) {
  const { data, setData, patch, processing, errors, clearErrors } = useForm({
    name: "",
    kind: kinds[0] || "blog",
    url: "",
    relevance_note: "",
  })

  useEffect(() => {
    if (!isOpen || !source) return

    setData("name", source.name || "")
    setData("kind", source.kind || kinds[0] || "blog")
    setData("url", source.url || "")
    setData("relevance_note", source.relevance_note || "")
    clearErrors()
  }, [clearErrors, isOpen, kinds, setData, source])

  const handleClose = () => {
    clearErrors()
    onClose()
  }

  const submit = (e) => {
    e.preventDefault()
    if (!source) return

    patch(
      api.organizationsMediaSources.update.path({
        org_nano_id: orgNanoId,
        id: source.id,
      }),
      {
        preserveScroll: true,
        onSuccess: () => {
          handleClose()
        },
      }
    )
  }

  if (!isOpen || !source) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]">
        <DialogHeader>
          <DialogTitle className='text-sm'>Edit Source</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="edit-source-name">Name</Label>
              <Input
                id="edit-source-name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="edit-source-kind">Kind</Label>
              <Select value={data.kind} onValueChange={(v) => setData("kind", v)}>
                <SelectTrigger id="edit-source-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {kinds.map((k) => (
                    <SelectItem key={k} value={k}>
                      {SOURCE_KIND_LABELS[k] || k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-source-url">URL</Label>
            <Input
              id="edit-source-url"
              value={data.url}
              onChange={(e) => setData("url", e.target.value)}
            />
            {errors.url && <p className="text-xs text-destructive">{errors.url}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="edit-source-note">Relevance note</Label>
            <Textarea
              id="edit-source-note"
              rows={2}
              value={data.relevance_note}
              onChange={(e) => setData("relevance_note", e.target.value)}
              placeholder="Why this source is relevant (optional)"
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={processing}>
              {processing ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function AddSourceModal({ isOpen, onClose, orgNanoId, kinds }) {
  const { data, setData, post, processing, errors, reset } = useForm({
    name: "",
    kind: kinds[0] || "blog",
    url: "",
    relevance_note: "",
    active: true,
  })

  const submit = (e) => {
    e.preventDefault()
    post(
      api.organizationsMediaSources.create.path({ org_nano_id: orgNanoId }),
      {
        preserveScroll: true,
        onSuccess: () => {
          reset()
          onClose()
        },
      }
    )
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]">
        <DialogHeader>
          <DialogTitle className='text-sm'>Add Custom Source</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="new-name">Name</Label>
              <Input
                id="new-name"
                value={data.name}
                onChange={(e) => setData("name", e.target.value)}
                placeholder="e.g. Stratechery"
              />
              {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="new-kind">Kind</Label>
              <Select value={data.kind} onValueChange={(v) => setData("kind", v)}>
                <SelectTrigger id="new-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {kinds.map((k) => (
                    <SelectItem key={k} value={k}>
                      {SOURCE_KIND_LABELS[k] || k}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-url">URL</Label>
            <Input
              id="new-url"
              value={data.url}
              onChange={(e) => setData("url", e.target.value)}
              placeholder="https://..."
            />
            {errors.url && <p className="text-xs text-destructive">{errors.url}</p>}
          </div>
          <div className="space-y-1">
            <Label htmlFor="new-note">Relevance note</Label>
            <Textarea
              id="new-note"
              rows={2}
              value={data.relevance_note}
              onChange={(e) => setData("relevance_note", e.target.value)}
              placeholder="Why this source is relevant (optional)"
            />
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" onClick={() => { reset(); onClose() }}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={processing}>
              {processing ? "Adding..." : "Add source"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function TopicSourcesGrid({ sources, orgNanoId, onEditSource }) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLargeScreen, setIsLargeScreen] = useState(false)

  useEffect(() => {
    if (typeof window === "undefined") return

    const mediaQuery = window.matchMedia("(min-width: 1024px)")
    const syncBreakpoint = () => setIsLargeScreen(mediaQuery.matches)

    syncBreakpoint()
    mediaQuery.addEventListener("change", syncBreakpoint)

    return () => {
      mediaQuery.removeEventListener("change", syncBreakpoint)
    }
  }, [])

  const collapsedItemCount = isLargeScreen ? 9 : 3
  const canExpand = sources.length > collapsedItemCount
  const visibleSources = isExpanded ? sources : sources.slice(0, collapsedItemCount)

  return (
    <div className="space-y-3">
      <div
        className={`grid grid-cols-1 gap-3 lg:grid-cols-3 transition-all duration-300 ${
          isExpanded ? "ease-in" : "ease-out"
        }`}
      >
        {visibleSources.map((source) => (
          <div
            key={`source-${source.id}`}
            className={`transition-all duration-300 ${
              isExpanded ? "animate-in fade-in-0 slide-in-from-bottom-2 ease-in" : "ease-out"
            }`}
          >
            <SourceRow
              source={source}
              orgNanoId={orgNanoId}
              onEdit={onEditSource}
            />
          </div>
        ))}
      </div>
      {canExpand && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            {isExpanded ? (
              <>
                <ChevronUp className="size-3.5" />
                Collapse
              </>
            ) : (
              <>
                <ChevronDown className="size-3.5" />
                Expand
              </>
            )}
          </button>
        </div>
      )}
    </div>
  )
}

function getHighlightPreview(highlight) {
  if (highlight.text?.trim()) return highlight.text.trim()
  if (highlight.kind === "meeting" || highlight.kind === "voice_note") {
    if (highlight.transcript === "processing") return "Processing transcript..."
    if (highlight.transcript?.trim()) return highlight.transcript.trim()
  }
  if (highlight.kind === "image") return "Image attachment"
  if (highlight.kind === "video") return "Video attachment"
  if (highlight.kind === "voice_note") return "Voice note recording"
  if (highlight.kind === "podcast") return "Audio recording"
  return "No preview available"
}

const HIGHLIGHT_CARD_META = {
  web_clip: { icon: Link, label: "Reading highlight" },
  voice_note: { icon: Mic, label: "Voice note" },
  podcast: { icon: Mic, label: "Audio" },
  note: { icon: FileText, label: "Note" },
  video: { icon: Video, label: "Video" },
  image: { icon: ImageIcon, label: "Image" },
  meeting: { icon: Video, label: "Meeting" },
}

function AudioPlayer({ url }) {
  const audioRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00"
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${m}:${sec.toString().padStart(2, "0")}`
  }

  const toggle = (e) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio) return
    isPlaying ? audio.pause() : audio.play()
  }

  const handleSeek = (e) => {
    e.stopPropagation()
    const audio = audioRef.current
    if (!audio || !duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    audio.currentTime = ((e.clientX - rect.left) / rect.width) * duration
  }

  const progress = duration ? (currentTime / duration) * 100 : 0

  return (
    <div
      className="flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onTimeUpdate={() => setCurrentTime(audioRef.current.currentTime)}
        onLoadedMetadata={() => setDuration(audioRef.current.duration)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => { setIsPlaying(false); setCurrentTime(0) }}
      />
      <button
        type="button"
        onClick={toggle}
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-foreground transition hover:bg-foreground/20"
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {isPlaying
          ? <Pause className="size-2.5" fill="currentColor" strokeWidth={0} />
          : <Play className="size-2.5 translate-x-px" fill="currentColor" strokeWidth={0} />}
      </button>
      <div
        className="relative h-1 flex-1 cursor-pointer overflow-hidden rounded-full bg-foreground/10"
        onClick={handleSeek}
      >
        <div className="h-full rounded-full bg-foreground/30 transition-[width]" style={{ width: `${progress}%` }} />
      </div>
      <span className="w-8 text-right text-[10px] tabular-nums text-muted-foreground">
        {currentTime > 0 ? fmt(currentTime) : fmt(duration)}
      </span>
    </div>
  )
}

function HighlightCard({ highlight, onEdit, onDelete }) {
  const preview = getHighlightPreview(highlight)
  const hasSource = Boolean(highlight.source)
  const imageUrl = highlight.kind === "image" ? highlight.asset?.file_url : null
  const videoUrl = highlight.kind === "video" ? highlight.asset?.file_url : null
  const audioUrl = (highlight.kind === "voice_note" || highlight.kind === "podcast")
    ? (highlight.asset?.file_url || highlight.recording_url || null)
    : null
  const [isMediaOpen, setIsMediaOpen] = useState(false)
  const [isAudioVisible, setIsAudioVisible] = useState(false)
  const cardMeta = HIGHLIGHT_CARD_META[highlight.kind] || { icon: FileText, label: "Idea" }
  const CardIcon = cardMeta.icon
  const previewMediaUrl = imageUrl || videoUrl
  const hasModalMedia = Boolean(previewMediaUrl)

  return (
    <>
      <div
        className={`relative flex h-auto cursor-pointer flex-col gap-3 overflow-hidden rounded-md border p-4 transition-[background-color,border-color,box-shadow] duration-200 hover:bg-accent/50 hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)] ${hasModalMedia ? "pl-[5.5rem]" : ""}`}
        onClick={onEdit}
      >
        {hasModalMedia && (
          <button
            type="button"
            onClick={(event) => {
              stopEvent(event)
              setIsMediaOpen(true)
            }}
            onPointerDown={stopEvent}
            onMouseDown={stopEvent}
            className="group absolute left-3 top-3 size-16 overflow-hidden rounded-sm border bg-muted transition-opacity hover:opacity-95"
            aria-label={`Open ${cardMeta.label.toLowerCase()} preview`}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Idea preview"
                className="h-full w-full object-cover"
              />
            ) : (
              <video
                src={videoUrl}
                muted
                playsInline
                preload="metadata"
                className="h-full w-full object-cover"
              >
                Your browser does not support the video tag.
              </video>
            )}
            {videoUrl && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/10 text-white transition-colors group-hover:bg-black/20">
                <Play className="size-3.5" fill="currentColor" />
              </div>
            )}
          </button>
        )}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-start gap-2">
            {!hasModalMedia && (
              <div className="flex size-5 shrink-0 items-center justify-center rounded-sm bg-muted text-muted-foreground">
                <CardIcon className="size-3" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="line-clamp-1 text-sm font-medium leading-5 whitespace-pre-wrap break-words">
                {preview}
              </div>
            </div>
          </div>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onPointerDown={stopEvent}
              onMouseDown={stopEvent}
              onClick={stopEvent}
            >
              <MoreHorizontal className="size-4" />
            </Button>
          </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-0" onCloseAutoFocus={stopEvent} onClick={stopEvent}>
              {audioUrl && (
                <DropdownMenuItem onSelect={(event) => {
                  event.preventDefault()
                  setIsAudioVisible((visible) => !visible)
                }}>
                  {isAudioVisible ? "Hide audio" : "Play audio"}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onSelect={(event) => {
                event.preventDefault()
                onEdit()
              }}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(event) => {
                  event.preventDefault()
                  onDelete()
                }}
                className="text-destructive focus:text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div
          className={
            hasModalMedia
              ? "absolute left-[5.5rem] right-4 top-[3.75rem] flex items-center justify-between gap-3"
              : "mt-auto flex items-center justify-between gap-3"
          }
        >
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Created {format(new Date(highlight.created_at), "MMMM d")}</span>
            {hasSource && (
              <a
                href={highlight.source}
                target="_blank"
                rel="noopener noreferrer"
                onClick={stopEvent}
                className="shrink-0 transition-colors hover:text-primary"
                title={highlight.source}
              >
                <Link className="size-3" />
              </a>
            )}
          </div>
        </div>

        {audioUrl && isAudioVisible && (
          <audio
            src={audioUrl}
            controls
            className="absolute bottom-3 left-4 right-4 z-10 h-8 w-[calc(100%-2rem)] rounded-md bg-black"
            style={{ colorScheme: "dark" }}
            onClick={stopEvent}
          >
            Your browser does not support the audio element.
          </audio>
        )}
      </div>

      {previewMediaUrl && (
        <Dialog open={isMediaOpen} onOpenChange={setIsMediaOpen}>
          <DialogContent className="max-w-4xl border-none bg-transparent p-0 shadow-none">
            <div className="overflow-hidden rounded-lg bg-black">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt="Idea preview"
                  className="h-auto max-h-[85vh] w-full object-contain"
                />
              ) : (
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  className="h-auto max-h-[85vh] w-full object-contain"
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}

export default function Index({
  highlights: initialHighlights,
  current_period,
  media_sources = [],
  kinds = [],
  topics_catalog = [],
  selected_topics = [],
  view_mode = "sources",
}) {
  const { organization } = usePage().props.auth
  const { errors: pageErrors } = usePage().props
  const selectedPeriod = current_period || "last_three_months"
  const isSourcesView = view_mode === "sources"
  const isIdeasView = view_mode === "ideas"
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [selectedKinds, setSelectedKinds] = useState([])
  const [selectedDateFilter, setSelectedDateFilter] = useState(selectedPeriod)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [highlightToEdit, setHighlightToEdit] = useState(null)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [highlightToDelete, setHighlightToDelete] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isAddSourceOpen, setIsAddSourceOpen] = useState(false)
  const [isEditSourceOpen, setIsEditSourceOpen] = useState(false)
  const [sourceToEdit, setSourceToEdit] = useState(null)
  const [isIdeaFiltersVisible, setIsIdeaFiltersVisible] = useState(false)
  const [isInlineVoiceModeVisible, setIsInlineVoiceModeVisible] = useState(false)
  const [isIdeaKindMenuOpen, setIsIdeaKindMenuOpen] = useState(false)
  const [isPresetMenuOpen, setIsPresetMenuOpen] = useState(false)
  const [selectedTopicPresets, setSelectedTopicPresets] = useState(selected_topics)
  const [voiceMemoPollState, setVoiceMemoPollState] = useState(null)

  useEffect(() => {
    setSelectedTopicPresets(selected_topics)
  }, [selected_topics])

  useEffect(() => {
    setSelectedDateFilter(selectedPeriod)
  }, [selectedPeriod])

  const handlePeriodChange = (value) => {
    setSelectedDateFilter(value)
    router.get(window.location.pathname, { period: value }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const handleIdeaFilterToggle = (kind) => {
    handleKindToggle(kind)
    setIsIdeaKindMenuOpen(true)
  }

  const handlePresetToggle = (slug) => {
    const next = selectedTopicPresets.includes(slug)
      ? selectedTopicPresets.filter((item) => item !== slug)
      : [...selectedTopicPresets, slug]

    setSelectedTopicPresets(next)
    setIsPresetMenuOpen(true)

    router.patch(
      api.organizationsMediaSources.updateTopics.path({ org_nano_id: organization.nano_id }),
      { topics: next },
      { preserveScroll: true, preserveState: true }
    )
  }

  const uploadFile = useCallback((file) => {
    setIsUploading(true)
    setUploadError(null)

    directUploadFile(file, {
      onSuccess: (blob) => {
        router.post(highlightsApi.index.path({ org_nano_id: organization.nano_id }), {
          highlight: {
            asset_attributes: {
              file: blob.signed_id,
            },
          },
        }, {
          preserveScroll: true,
          onSuccess: () => {
            toast.success(`${file.name} processed successfully.`)
            setUploadError(null)
          },
          onError: (errors) => {
            setUploadError(`Error creating highlight for ${file.name}: ${getErrorMessage(errors)}`)
            toast.error(`Failed to process ${file.name}.`)
          },
          onFinish: () => {
            setIsUploading(false)
          },
        })
      },
      onError: (error) => {
        setUploadError(`Error uploading ${file.name}: ${error}`);
        setIsUploading(false)
        toast.error(`Failed to upload ${file.name}.`)
      },
    })
  }, [organization.nano_id])

  const onDrop = useCallback(acceptedFiles => {
    acceptedFiles.forEach(file => {
      uploadFile(file)
    })
  }, [uploadFile])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploading
  })

  const handleKindToggle = (kind) => {
    setSelectedKinds(prev =>
      prev.includes(kind)
        ? prev.filter(k => k !== kind)
        : [...prev, kind]
    )
  }

  const filteredHighlights = useMemo(() => {
    if (selectedKinds.length === 0) {
      return initialHighlights
    }
    return initialHighlights.filter(highlight =>
      selectedKinds.includes(highlight.kind)
    )
  }, [initialHighlights, selectedKinds])

  const voiceNoteCount = useMemo(
    () => initialHighlights.filter((highlight) => highlight.kind === "voice_note").length,
    [initialHighlights]
  )

  useEffect(() => {
    if (!voiceMemoPollState) return

    if (voiceNoteCount > voiceMemoPollState.baselineCount) {
      setVoiceMemoPollState(null)
      setIsAddModalOpen(false)
      setIsInlineVoiceModeVisible(false)
      toast.success("Voice memo added.")
      return
    }

    if (voiceMemoPollState.attempts >= VOICE_MEMO_POLL_MAX_ATTEMPTS) {
      setVoiceMemoPollState(null)
      return
    }

    const timeoutId = window.setTimeout(() => {
      router.reload({
        only: ["highlights"],
        preserveScroll: true,
        preserveState: true,
      })
      setVoiceMemoPollState((current) => (
        current
          ? { ...current, attempts: current.attempts + 1 }
          : current
      ))
    }, VOICE_MEMO_POLL_MS)

    return () => window.clearTimeout(timeoutId)
  }, [voiceMemoPollState, voiceNoteCount])

  const openEditModal = (highlight) => {
    setHighlightToEdit(highlight)
    setIsEditModalOpen(true)
  }

  const closeEditModal = () => {
    setHighlightToEdit(null)
    setIsEditModalOpen(false)
  }

  const openEditSourceModal = (source) => {
    setSourceToEdit(source)
    setIsEditSourceOpen(true)
  }

  const closeEditSourceModal = () => {
    setSourceToEdit(null)
    setIsEditSourceOpen(false)
  }

  const handleSaveText = (highlightId, newText, file, removeExistingMedia) => {
    setIsSaving(true)

    const submitUpdate = (assetData = null) => {
      const payload = { highlight: { text: newText } }
      
      if (assetData) {
        payload.highlight.asset_attributes = assetData
      } else if (removeExistingMedia) {
        payload.highlight.remove_asset = true
      }

      router.patch(highlightsApi.update.path({ org_nano_id: organization.nano_id, id: highlightId }), payload, {
        preserveScroll: true,
        onSuccess: () => {
          toast.success('Content updated successfully.')
          closeEditModal()
        },
        onError: (errors) => {
          toast.error(`Failed to update: ${getErrorMessage(errors)}`)
        },
        onFinish: () => {
          setIsSaving(false)
        },
      })
    }

    if (file) {
      directUploadFile(file, {
        onSuccess: (blob) => submitUpdate({ file: blob.signed_id }),
        onError: (error) => {
          toast.error(`Failed to upload file: ${error}`)
          setIsSaving(false)
        },
      })
    } else {
      submitUpdate();
    }
  }

  const openDeleteModal = (highlight) => {
    setHighlightToDelete(highlight)
    setIsDeleteModalOpen(true)
  }

  const closeDeleteModal = () => {
    setHighlightToDelete(null)
    setIsDeleteModalOpen(false)
  }

  const handleDeleteHighlight = () => {
    if (!highlightToDelete) return
    
    router.delete(highlightsApi.destroy.path({ org_nano_id: organization.nano_id, id: highlightToDelete.id }), {
      preserveScroll: true,
      onSuccess: () => {
        toast.success('Highlight deleted.')
        closeDeleteModal()
      },
      onError: () => {
        toast.error('Failed to delete highlight.')
      },
    });
  }

  const handleAddHighlight = (noteText, file) => {
    setIsSubmitting(true)

    if (file) {
      directUploadFile(file, {
        onSuccess: (blob) => {
          router.post(highlightsApi.index.path({ org_nano_id: organization.nano_id }), {
            highlight: {
              text: noteText || null,
              asset_attributes: {
                file: blob.signed_id,
              },
            },
          }, {
            preserveScroll: true,
            onSuccess: () => {
              toast.success('Highlight added successfully.')
              setIsAddModalOpen(false)
            },
            onError: (errors) => {
              toast.error(`Failed to add highlight: ${getErrorMessage(errors)}`)
            },
            onFinish: () => {
              setIsSubmitting(false)
            },
          })
        },
        onError: (error) => {
          toast.error(`Failed to upload file: ${error}`)
          setIsSubmitting(false)
        },
      })
    } else {
      router.post(highlightsApi.index.path({ org_nano_id: organization.nano_id }), {
        highlight: {
          text: noteText,
          kind: 'note',
        },
      }, {
        preserveScroll: true,
        onSuccess: () => {
          toast.success('Highlight added successfully.')
          setIsAddModalOpen(false)
        },
        onError: (errors) => {
          toast.error(`Failed to add highlight: ${getErrorMessage(errors)}`)
        },
        onFinish: () => {
          setIsSubmitting(false)
        },
      })
    }
  }

  const hasSources = media_sources.length > 0
  const hasIdeas = initialHighlights.length > 0

  const handleVoiceCallEnd = () => {
    setVoiceMemoPollState({
      baselineCount: voiceNoteCount,
      attempts: 0,
    })
  }

  return (
    <Layout>
      <div className="space-y-6 pb-8">
        <PageHeader title={isSourcesView ? "Recurring Sources" : "Your Ideas"}>
          {isSourcesView ? (
            <div className="flex items-center gap-1.5">
              <Button size="sm" className="rounded-sm" onClick={() => setIsAddSourceOpen(true)}>
                Add Source
              </Button>
              <DropdownMenu modal={false} open={isPresetMenuOpen} onOpenChange={setIsPresetMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline">
                    Manage Topics
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[200px]">
                  {topics_catalog.map((slug) => (
                    <DropdownMenuCheckboxItem
                      key={slug}
                      checked={selectedTopicPresets.includes(slug)}
                      onSelect={(event) => event.preventDefault()}
                      onCheckedChange={() => handlePresetToggle(slug)}
                    >
                      {TOPIC_META[slug]?.label || slug}
                    </DropdownMenuCheckboxItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex flex-col items-end">
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <Button size="sm" className="rounded-sm" onClick={() => setIsAddModalOpen(true)}>
                  Add Idea
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsInlineVoiceModeVisible((visible) => !visible)}
                >
                  Voice Mode
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsIdeaFiltersVisible((visible) => !visible)}
                >
                  Filter
                </Button>
              </div>
              <div
                className={`w-full overflow-hidden transition-all duration-500 ${
                  isInlineVoiceModeVisible
                    ? "mt-2 max-h-24 translate-y-0 opacity-100 ease-in-out"
                    : "mt-0 max-h-0 -translate-y-1 opacity-0 pointer-events-none ease-in-out"
                }`}
              >
                <div className="flex justify-end">
                  <div className="w-full max-w-[320px]">
                    <VoiceNoteWidget onRecordingCreated={handleVoiceCallEnd} />
                  </div>
                </div>
              </div>
              <div
                className={`flex items-center gap-1.5 overflow-hidden transition-all duration-200 ${
                  isIdeaFiltersVisible
                    ? "mt-1 max-h-12 translate-y-0 pt-0.5 pb-0 opacity-100 ease-in"
                    : "mt-0 max-h-0 -translate-y-1 opacity-0 pointer-events-none ease-out"
                }`}
              >
                <DropdownMenu modal={false}>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="h-7 rounded-full px-2.5 text-xs">
                      By Date
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[180px]">
                    {periodOptions.map((option) => (
                      <DropdownMenuCheckboxItem
                        key={option.value}
                        checked={selectedDateFilter === option.value}
                        onSelect={(event) => event.preventDefault()}
                        onCheckedChange={() => handlePeriodChange(option.value)}
                      >
                        {option.label}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                <DropdownMenu modal={false} open={isIdeaKindMenuOpen} onOpenChange={setIsIdeaKindMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button type="button" variant="outline" size="sm" className="h-7 rounded-full px-2.5 text-xs">
                      By Kind
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[180px]">
                    {IDEA_FILTER_KINDS.map((kind) => (
                      <DropdownMenuCheckboxItem
                        key={kind}
                        checked={selectedKinds.includes(kind)}
                        onSelect={(event) => event.preventDefault()}
                        onCheckedChange={() => handleIdeaFilterToggle(kind)}
                      >
                        {KIND_META[kind]?.label || kind}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          )}
        </PageHeader>

        {(uploadError || (pageErrors && pageErrors.length > 0)) && (
          <div className="space-y-1 rounded-md border border-destructive/20 bg-destructive/5 px-4 py-3 text-sm text-destructive">
            {uploadError && <p>{uploadError}</p>}
            {pageErrors?.map((error, index) => (
              <p key={`page-error-${index}`}>{error}</p>
            ))}
          </div>
        )}

        {isSourcesView ? (
          hasSources ? (
            <section>
              <TopicSourcesGrid
                sources={media_sources}
                orgNanoId={organization.nano_id}
                onEditSource={openEditSourceModal}
              />
            </section>
          ) : (
            <EmptyState className="min-h-[220px]">
              Add a source or select some topics to get started.
            </EmptyState>
          )
        ) : (
          <section className="space-y-3">
            {filteredHighlights.length > 0 ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                {filteredHighlights.map((highlight) => (
                  <HighlightCard
                    key={`highlight-${highlight.id}`}
                    highlight={highlight}
                    onEdit={() => openEditModal(highlight)}
                    onDelete={() => openDeleteModal(highlight)}
                  />
                ))}
              </div>
            ) : (
              <EmptyState>No ideas match the selected filters.</EmptyState>
            )}
          </section>
        )}
      </div>
      <EditTextModal
        isOpen={isEditModalOpen}
        highlight={highlightToEdit}
        onClose={closeEditModal}
        onSave={handleSaveText}
        isSaving={isSaving}
      />
      <AddHighlightModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={handleAddHighlight}
        isSubmitting={isSubmitting}
      />
      <AddSourceModal
        isOpen={isAddSourceOpen}
        onClose={() => setIsAddSourceOpen(false)}
        orgNanoId={organization.nano_id}
        kinds={kinds}
      />
      <EditSourceModal
        isOpen={isEditSourceOpen}
        source={sourceToEdit}
        onClose={closeEditSourceModal}
        orgNanoId={organization.nano_id}
        kinds={kinds}
      />
      <Dialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className='text-sm'>Delete Highlight</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete this highlight? This action cannot be undone.
          </p>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteHighlight}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  )
}
